import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaGift,
  FaPlane,
  FaHotel,
  FaMapMarkedAlt,
  FaStar,
  FaTimes,
} from 'react-icons/fa';
import { useSite } from '../../context/SiteContext';
import { useTravelContext } from '../../context/TravelContext';
import { isRedirectShortlinkPath } from '../../utils/redirectShortlink';
import './DealPopups.css';

const POPUP_DELAY = {
  onLoad: 3000,
  between: 4000,
  repeatCycle: 4500,
};

const EXIT_MS = 420;

function pickFeaturedDeal(destinations) {
  if (!destinations?.length) return null;
  const trending = destinations.find((d) => d.tag === 'trending');
  const cheapest = [...destinations].sort((a, b) => (a.price || 0) - (b.price || 0))[0];
  return trending || cheapest || destinations[0];
}

function getNextPopupType(current, featuredDeal) {
  if (!current || current === 'right') return 'center';
  if (current === 'center') return featuredDeal ? 'left' : 'right';
  if (current === 'left') return 'right';
  return 'center';
}

function getPopupDelayAfter(current) {
  return current === 'right' ? POPUP_DELAY.repeatCycle : POPUP_DELAY.between;
}

function formatPrice(price) {
  if (!price) return 'Special rate';
  return `$${Number(price).toLocaleString()}`;
}

const DealPopups = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { siteName } = useSite();
  const { destinations } = useTravelContext();
  const [activeType, setActiveType] = useState(null);
  const [phase, setPhase] = useState('hidden');
  const timersRef = useRef({ load: null, chain: null, exit: null });

  const featuredDeal = useMemo(() => pickFeaturedDeal(destinations), [destinations]);

  const clearTimer = useCallback((key) => {
    const id = timersRef.current[key];
    if (id) {
      window.clearTimeout(id);
      timersRef.current[key] = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    Object.keys(timersRef.current).forEach((key) => clearTimer(key));
  }, [clearTimer]);

  const canShow = useMemo(() => {
    if (location.pathname.includes('/admin')) return false;
    if (isRedirectShortlinkPath(location.pathname)) return false;
    return true;
  }, [location.pathname]);

  const resolvePopupType = useCallback(
    (type) => {
      if (type === 'left' && !featuredDeal) return 'right';
      return type;
    },
    [featuredDeal]
  );

  const openPopup = useCallback(
    (type) => {
      const resolved = resolvePopupType(type);
      setActiveType(resolved);
      setPhase('enter');
    },
    [resolvePopupType]
  );

  const scheduleNextPopup = useCallback(
    (currentType) => {
      clearTimer('chain');
      const next = getNextPopupType(currentType, featuredDeal);
      const delay = getPopupDelayAfter(currentType);
      timersRef.current.chain = window.setTimeout(() => {
        timersRef.current.chain = null;
        openPopup(next);
      }, delay);
    },
    [featuredDeal, openPopup, clearTimer]
  );

  const dismissPopup = useCallback(
    (type) => {
      clearTimer('chain');
      clearTimer('exit');
      setPhase('exit');
      timersRef.current.exit = window.setTimeout(() => {
        timersRef.current.exit = null;
        setPhase('hidden');
        setActiveType(null);
        scheduleNextPopup(type);
      }, EXIT_MS);
    },
    [scheduleNextPopup, clearTimer]
  );

  const openPopupRef = useRef(openPopup);
  openPopupRef.current = openPopup;

  useEffect(() => {
    if (activeType !== 'left' || featuredDeal || phase === 'hidden') return undefined;
    dismissPopup('left');
    return undefined;
  }, [activeType, featuredDeal, phase, dismissPopup]);

  useEffect(() => {
    if (!canShow) {
      clearAllTimers();
      setActiveType(null);
      setPhase('hidden');
      return undefined;
    }

    clearTimer('load');
    timersRef.current.load = window.setTimeout(() => {
      timersRef.current.load = null;
      openPopupRef.current('center');
    }, POPUP_DELAY.onLoad);

    return () => clearTimer('load');
  }, [canShow, clearTimer, clearAllTimers]);

  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  if (!canShow || !activeType || phase === 'hidden') return null;

  const phaseClass = phase === 'exit' ? 'is-exit' : 'is-enter';

  if (activeType === 'center') {
    return (
      <div
        className={`deal-popup deal-popup--center ${phaseClass}`}
        role="presentation"
        onMouseDown={(e) => e.target === e.currentTarget && dismissPopup('center')}
      >
        <div className="deal-popup__backdrop" aria-hidden />
        <div className="deal-popup__card deal-popup__card--center deal-popup__card--mega" role="dialog" aria-modal="true">
          <button type="button" className="deal-popup__close deal-popup__close--light" aria-label="Close" onClick={() => dismissPopup('center')}>
            <FaTimes />
          </button>

          <div className="deal-popup__mega-banner deal-popup__stagger" style={{ '--i': 0 }}>
            <div className="deal-popup__mega-glow" aria-hidden />
            <div className="deal-popup__mega-spark deal-popup__mega-spark--1" aria-hidden />
            <div className="deal-popup__mega-spark deal-popup__mega-spark--2" aria-hidden />
            <span className="deal-popup__flash-badge">
              <FaGift aria-hidden /> Flash sale · Ends soon
            </span>
            <div className="deal-popup__mega-deal">
              <span className="deal-popup__mega-prefix">Up to</span>
              <div className="deal-popup__mega-number" aria-label="50 percent off">
                <span className="deal-popup__mega-percent">50</span>
                <span className="deal-popup__mega-off">
                  <span className="deal-popup__mega-symbol">%</span>
                  <span className="deal-popup__mega-text">OFF</span>
                </span>
              </div>
              <span className="deal-popup__mega-tagline">Flights · Hotels · Packages</span>
            </div>
          </div>

          <div className="deal-popup__mega-body">
            <h2 className="deal-popup__mega-title deal-popup__stagger" style={{ '--i': 1 }}>
              Your biggest travel savings with <em>{siteName}</em>
            </h2>
            <p className="deal-popup__mega-lead deal-popup__stagger" style={{ '--i': 2 }}>
              Lock in member-only rates today — limited seats at this price.
            </p>

            <ul className="deal-popup__perks deal-popup__perks--mega">
              <li className="deal-popup__stagger" style={{ '--i': 3 }}>
                <span className="deal-popup__perk-icon"><FaPlane /></span>
                <span>Flights from <strong>$49</strong></span>
              </li>
              <li className="deal-popup__stagger" style={{ '--i': 4 }}>
                <span className="deal-popup__perk-icon"><FaHotel /></span>
                <span>Hotels <strong>50% off</strong></span>
              </li>
              <li className="deal-popup__stagger" style={{ '--i': 5 }}>
                <span className="deal-popup__perk-icon"><FaMapMarkedAlt /></span>
                <span><strong>250+</strong> destinations</span>
              </li>
            </ul>

            <div className="deal-popup__actions deal-popup__actions--mega deal-popup__stagger" style={{ '--i': 6 }}>
              <button
                type="button"
                className="deal-popup__btn deal-popup__btn--mega"
                onClick={() => {
                  dismissPopup('center');
                  navigate('/flight');
                }}
              >
                Claim 50% Off <FaArrowRight />
              </button>
              <button type="button" className="deal-popup__btn deal-popup__btn--ghost" onClick={() => dismissPopup('center')}>
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (activeType === 'left' && featuredDeal) {
    return (
      <aside className={`deal-popup deal-popup--left ${phaseClass}`} aria-live="polite">
        <div className="deal-popup__card deal-popup__card--side" role="dialog" aria-modal="true">
          <button type="button" className="deal-popup__close" aria-label="Close" onClick={() => dismissPopup('left')}>
            <FaTimes />
          </button>
          <span className="deal-popup__eyebrow deal-popup__eyebrow--hot deal-popup__stagger" style={{ '--i': 0 }}>
            Hot package deal
          </span>
          <div className="deal-popup__deal-row deal-popup__stagger" style={{ '--i': 1 }}>
            <img src={featuredDeal.image} alt={featuredDeal.name} className="deal-popup__deal-img" />
            <div>
              <h3>{featuredDeal.name}</h3>
              <p>{featuredDeal.short}</p>
              <div className="deal-popup__meta">
                <span>{formatPrice(featuredDeal.price)}</span>
                <span><FaStar /> {featuredDeal.duration}</span>
              </div>
            </div>
          </div>
          <div className="deal-popup__actions deal-popup__stagger" style={{ '--i': 2 }}>
            <button
              type="button"
              className="deal-popup__btn deal-popup__btn--primary"
              onClick={() => {
                dismissPopup('left');
                navigate('/packages');
              }}
            >
              Book package <FaArrowRight />
            </button>
          </div>
        </div>
      </aside>
    );
  }

  if (activeType === 'right') {
    return (
      <aside className={`deal-popup deal-popup--right ${phaseClass}`} aria-live="polite">
        <div className="deal-popup__card deal-popup__card--side deal-popup__card--call" role="dialog" aria-modal="true">
          <button type="button" className="deal-popup__close" aria-label="Close" onClick={() => dismissPopup('right')}>
            <FaTimes />
          </button>
          <span className="deal-popup__eyebrow deal-popup__stagger" style={{ '--i': 0 }}>
            <FaPlane /> Flash fare alert
          </span>
          <h3 className="deal-popup__side-title deal-popup__stagger" style={{ '--i': 1 }}>
            Unlock member-only rates
          </h3>
          <p className="deal-popup__side-text deal-popup__stagger" style={{ '--i': 2 }}>
            Get unpublished flight & hotel discounts — our team is available 24/7.
          </p>
          <div className="deal-popup__actions deal-popup__stagger" style={{ '--i': 3 }}>
            <button
              type="button"
              className="deal-popup__btn deal-popup__btn--primary"
              onClick={() => {
                dismissPopup('right');
                navigate('/contact');
              }}
            >
              Contact us <FaArrowRight />
            </button>
            <button type="button" className="deal-popup__btn deal-popup__btn--ghost" onClick={() => dismissPopup('right')}>
              Maybe later
            </button>
          </div>
        </div>
      </aside>
    );
  }

  return null;
};

export default DealPopups;
